import * as React from 'react';
import { useIntl } from 'react-intl';
import {
  CmdInput,
  CmdLabel,
  CmdRadioButton,
  CmdSimpleSelect,
  CmdSubpage,
  CmdSubpageButton,
  CmdSwitch,
} from '@commander-services/gui-components';
import CmdBox from '@commander-services/cmd-box';
import { Controller, useForm } from 'react-hook-form';
import FormikValidatorService from '../../Services/FormikValidatorService';
import usePageWithForm from '../../hooks/usePageWithForm';
import OnlineHelp from '../OnlineHelp';
import { IOption } from '../Forms/CmdField';
import { transformErrors } from '../../Services/HookFormService';
import useAnalytics from '../../hooks/useAnalytics';

interface FormProps {
  closeCallback: () => void;
  reportsList: IOption[];
  handleReportId: (id: string, name: string, active: number) => void;
  recipientsBox: React.ReactNode;
  isActive: number;
  reportName: string;
}

interface IReportInputForm {
  frequency: string;
  active: number;
  name: string;
  reportId: string;
  format: string;
  date: Date[];
}

export default function Form(props: FormProps): JSX.Element {
  usePageWithForm();
  const { formatMessage: f } = useIntl();
  const { trackEvent } = useAnalytics();

  const validateFields = (values: IReportInputForm) => {
    let formErrors = {};
    formErrors = FormikValidatorService.validateByValidationApiResponse(values, {
      reportId: [
        {
          param: 'required',
          value: true,
          message: f({ id: 'form.required' }),
        },
      ],
      name: [
        {
          param: 'required',
          value: true,
          message: f({ id: 'form.required' }),
        },
      ],
      frequency: [
        {
          param: 'required',
          value: true,
          message: f({ id: 'form.required' }),
        },
      ],
    });

    return transformErrors(formErrors);
  };

  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = useForm<IReportInputForm>({
    mode: 'onChange',
    defaultValues: {
      active: props.isActive,
      frequency: '',
      name: props.reportName,
      reportId: '',
      format: '',
    },
    resolver: async (data) => {
      const formErrors = validateFields(data);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const onSubmit = () => {
    trackEvent(['auto reports', 'click', 'Autoreports basic form save']);
    return true;
  };

  const subpageButtons: CmdSubpageButton[] = [
    {
      id: 'autoreport-form-close',
      type: 'button',
      className: 'e-button',
      title: f({ id: 'suppliersForm.close' }),
      closeCallback: props.closeCallback,
    },
    {
      id: 'autoreport-form-submit',
      type: 'submit',
      className: 'e-button e-button--gray',
      title: f({ id: 'suppliersForm.save' }),
      submitCallback: handleSubmit(onSubmit),
    },
  ];

  return (
    <CmdSubpage
      id="autoreports-form"
      title={f({ id: 'menu.autoReports' })}
      subtitle={f({ id: 'table.add' })}
      buttons={subpageButtons}
      isNavigatorEnabled={false}
      maintitle={f({ id: 'menu.reports' })}
      onlineHelp={<OnlineHelp topic="add-auto-report" />}
    >
      <div>
        <div className="row">
          <div className="col-xxl-6">
            <CmdBox
              id="alarms-basic-information"
              title={f({ id: 'alarms.section.basicInformation' })}
            >
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <CmdInput
                    {...field}
                    id="name"
                    label={f({ id: 'form.title' })}
                    type="text"
                    required={true}
                    onChangeCallback={(value) => {
                      setValue('name', value);
                      trigger('name');
                    }}
                    error={errors.name?.message}
                    touched={true}
                  />
                )}
              />

              <Controller
                name="reportId"
                control={control}
                rules={{ required: errors.reportId ? errors.reportId.message : undefined }}
                render={({ field }) => (
                  <CmdSimpleSelect
                    {...field}
                    name="reportId"
                    id="reportId"
                    label={f({ id: 'reports.reportId' })}
                    introOption={f({ id: 'suppliersForm.choose' })}
                    options={props.reportsList}
                    required={true}
                    onChangeCallback={(value: string) => {
                      setValue('reportId', value);
                      props.handleReportId(value, watch('name'), watch('active'));
                      trackEvent(['auto reports', 'change', 'Autoreports basic form reportId']);
                    }}
                    error={errors.reportId?.message}
                    touched={true}
                  />
                )}
              />
            </CmdBox>
            <CmdBox
              id="reports-sending-settings"
              title={f({ id: 'reports.section.sendingSettings' })}
            >
              <Controller
                name="active"
                control={control}
                rules={{
                  required: errors.active ? errors.active.message : undefined,
                }}
                render={({ field }) => (
                  <CmdSwitch
                    {...field}
                    id="active"
                    label={f({ id: 'suppliersForm.active' })}
                    value={watch('active')}
                    setValue={(value: number) => {
                      setValue('active', value);
                      trigger('active');
                      trackEvent(['auto reports', 'change', 'Autoreports basic form active']);
                    }}
                  />
                )}
              />

              <Controller
                name="frequency"
                control={control}
                rules={{ required: errors.frequency ? errors.frequency.message : undefined }}
                render={({ field }) => (
                  <CmdSimpleSelect
                    {...field}
                    name="frequency"
                    id="frequency"
                    label={f({ id: 'reports.frequency' })}
                    introOption={f({ id: 'reports.chooseFrequency' })}
                    options={[]}
                    required={true}
                    error={errors.frequency?.message}
                    touched={true}
                  />
                )}
              />
              <Controller
                name="format"
                control={control}
                render={({ field }) => (
                  <CmdRadioButton
                    {...field}
                    id="format"
                    name="format"
                    label={f({ id: 'reports.format' })}
                    options={[{ item: '.xlsx', value: 'xlsx' }]}
                    required={true}
                    disabled={true}
                  />
                )}
              />
            </CmdBox>
          </div>

          <div className="col-xxl-6">
            <CmdBox id="reports-sending-settings" title={f({ id: 'reports.reportSettings' })}>
              <CmdLabel
                title={f({ id: 'reports.parametersSetting' })}
                value={f({ id: 'reports.parametersInfo' })}
                isLabelInTwoRows={true}
              />
            </CmdBox>

            {props.recipientsBox}
          </div>
        </div>
      </div>
    </CmdSubpage>
  );
}
